import { lazy, ComponentType } from 'react';
import { CasesUserRole } from '../hooks/useCasesUserRole';
import { CASES_ROLES } from '../constant/roles';
import { RoleBasedPageConfig } from '../types/permissions.type';

const CasesHomePage = lazy(() => import('@@/products/cases/HomePage'));
const CustomerCasesTablePage = lazy(() => import('@@/products/cases/pages/CustomerCaseTablePage'));
const CaseDetailPage = lazy(() => import('@@/products/cases/pages/CaseDetailPage'));
const CustomerCaseDetailPage = lazy(() => import('@@/products/cases/pages/CustomerCaseDetailPage'));
const CaseCreateUpdatePage = lazy(() => import('@@/products/cases/pages/CaseCreateUpdatePage'));
const TemplatesPage = lazy(() => import('@@/products/cases/pages/TemplatesPage'));
const TemplateCreateUpdatePage = lazy(() => import('@@/products/cases/pages/TemplateCreateUpdatePage'));
const ContactsManagementPage = lazy(() => import('@@/products/cases/pages/ContactsPage'));
const ProfileDetailPage = lazy(() => import('@@/products/cases/pages/ProfileDetailPage'));
const ProfileCreateUpdatePage = lazy(() => import('@@/products/cases/pages/ProfileCreateUpdatePage'));
const CompanyDetailPage = lazy(() => import('@@/products/cases/pages/CompanyDetailPage'));
export const ALL_CASES_ROLES = [
  CASES_ROLES.ALL_CASES_PERMISSION,
  CASES_ROLES.COMPANY_CASES_PERMISSION,
  CASES_ROLES.MY_CASES_PERMISSION
] as const;

export const ADMIN_ROLES = [CASES_ROLES.ALL_CASES_PERMISSION, CASES_ROLES.COMPANY_CASES_PERMISSION] as const;

export const SUPER_ADMIN_ONLY = [CASES_ROLES.ALL_CASES_PERMISSION] as const;
export const CUSTOMER_ONLY = [CASES_ROLES.MY_CASES_PERMISSION] as const;

export const pagePermissionMapping: Record<string, RoleBasedPageConfig> = {
  ALL_CASES: {
    id: 'all-cases',
    pages: [
      { component: CasesHomePage, allowedRoles: ADMIN_ROLES },
      { component: CustomerCasesTablePage, allowedRoles: CUSTOMER_ONLY }
    ],
    loadingMessage: 'Loading cases...'
  },

  CASE_DETAIL: {
    id: 'case-detail',
    pages: [
      { component: CaseDetailPage, allowedRoles: ADMIN_ROLES },
      { component: CustomerCaseDetailPage, allowedRoles: CUSTOMER_ONLY }
    ],
    loadingMessage: 'Loading case details...'
  },

  CASE_CREATE_UPDATE: {
    id: 'case-create-update',
    pages: [{ component: CaseCreateUpdatePage, allowedRoles: ADMIN_ROLES }],
    loadingMessage: 'Loading case form...'
  },

  TEMPLATES_LIST: {
    id: 'templates-list',
    pages: [{ component: TemplatesPage, allowedRoles: SUPER_ADMIN_ONLY }],
    loadingMessage: 'Loading templates...'
  },

  TEMPLATE_CREATE_UPDATE: {
    id: 'template-create-update',
    pages: [{ component: TemplateCreateUpdatePage, allowedRoles: SUPER_ADMIN_ONLY }],
    loadingMessage: 'Loading template form...'
  },

  CONTACTS_MANAGEMENT: {
    id: 'contacts-management',
    pages: [{ component: ContactsManagementPage, allowedRoles: ADMIN_ROLES }],
    loadingMessage: 'Loading profiles...'
  },
  PROFILE_DETAIL: {
    id: 'profile-detail',
    pages: [{ component: ProfileDetailPage, allowedRoles: ADMIN_ROLES }],
    loadingMessage: 'Loading profile details...'
  },
  PROFILE_CREATE_UPDATE: {
    id: 'profile-create-update',
    pages: [{ component: ProfileCreateUpdatePage, allowedRoles: ADMIN_ROLES }],
    loadingMessage: 'Loading profile form...'
  },
  COMPANY_DETAIL: {
    id: 'company-detail',
    pages: [{ component: CompanyDetailPage, allowedRoles: SUPER_ADMIN_ONLY }],
    loadingMessage: 'Loading company details...'
  }
};

export function getComponentForRole(config: RoleBasedPageConfig, role: CasesUserRole): ComponentType | null {
  if (!role || role === CASES_ROLES.NO_ACCESS) {
    return null;
  }

  const pageMapping = config.pages.find((page) => {
    if (page.allowedRoles === 'all') {
      return true;
    }
    return page.allowedRoles.includes(role as NonNullable<CasesUserRole>);
  });

  return pageMapping?.component || null;
}

export default pagePermissionMapping;
